import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Globe,
  Lock,
  Bell,
  Save,
  Camera,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Shield,
  Smartphone,
  X,
  Copy,
  Check,
  Clock
} from 'lucide-react';
import { userService } from '../../../services/userService';
import { companyService, COMPANY_SIZE_OPTIONS, INDUSTRY_OPTIONS, TIMEZONE_OPTIONS } from '../../../services/companyService';
import { twoFactorService } from '../../../services/twoFactorService';
import { notificationService } from '../../../services/notificationService';
import { useCompany } from '../../../contexts/CompanyContext';
import { getTimezoneForCountry } from '../../../utils/countryTimezoneMap';

const WEEKDAY_OPTIONS = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

const AccountSettings = () => {
  const { t, i18n } = useTranslation();
  const { updateCompany: updateCompanyContext, updatePreferences, refreshCompany } = useCompany();
  const [activeTab, setActiveTab] = useState('profile');
  const photoInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    bio: '',
    profilePhotoUrl: '',
    timezone: '',
  });

  // Company state
  const [company, setCompany] = useState({
    name: '',
    logoUrl: '',
    industry: '',
    size: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    website: '',
    timezone: '',
  });
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);

  // Security state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // 2FA Setup state
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Notification Preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailEnabled: true,
    workOrderUpdates: true,
    maintenanceReminders: true,
    assetAlerts: true,
    subscriptionAlerts: true,
    weeklyReports: false,
    weeklyReportDay: 'MONDAY',
    weeklyReportTime: '09:00',
    systemAnnouncements: true,
  });

  // Display Preferences state
  const [displayPrefs, setDisplayPrefs] = useState({
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
  });

  const tabs = [
    { id: 'profile', label: t('account.profile'), icon: User },
    { id: 'company', label: t('account.companyInfo'), icon: Building },
    { id: 'security', label: t('account.security'), icon: Lock },
    { id: 'preferences', label: t('account.preferences'), icon: Bell },
  ];

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [userRes, companyRes] = await Promise.all([
        userService.getCurrentUser(),
        companyService.getCurrentCompany(),
      ]);

      // Set profile data
      setProfile({
        firstName: userRes.firstName || '',
        lastName: userRes.lastName || '',
        email: userRes.email || '',
        phone: userRes.phone || '',
        jobTitle: userRes.jobTitle || '',
        bio: userRes.bio || '',
        profilePhotoUrl: userRes.profilePhotoUrl || '',
        timezone: userRes.timezone || '',
      });
      setTwoFactorEnabled(userRes.twoFactorEnabled || false);
      setIsCompanyAdmin(userRes.companyRole === 'COMPANY_ADMIN' || userRes.companyRole === 'COMPANY_OWNER');

      // Set company data (including timezone)
      setCompany({
        name: companyRes.name || '',
        logoUrl: companyRes.logoUrl || '',
        industry: companyRes.industry || '',
        size: companyRes.size || '',
        address: companyRes.address || '',
        city: companyRes.city || '',
        state: companyRes.state || '',
        zipCode: companyRes.zipCode || '',
        country: companyRes.country || '',
        website: companyRes.website || '',
        timezone: companyRes.timezone || '',
      });

      // Load notification preferences from API
      try {
        const prefsRes = await notificationService.getPreferences();
        setNotificationPrefs({
          emailEnabled: prefsRes.emailEnabled ?? true,
          workOrderUpdates: prefsRes.workOrderUpdates ?? true,
          maintenanceReminders: prefsRes.maintenanceReminders ?? true,
          assetAlerts: prefsRes.assetAlerts ?? true,
          subscriptionAlerts: prefsRes.subscriptionAlerts ?? true,
          weeklyReports: prefsRes.weeklyReports ?? false,
          weeklyReportDay: prefsRes.weeklyReportDay ?? 'MONDAY',
          weeklyReportTime: prefsRes.weeklyReportTime ?? '09:00',
          systemAnnouncements: prefsRes.systemAnnouncements ?? true,
        });
      } catch (err) {
        console.log('Using default notification preferences');
      }

      // Load display preferences from localStorage.
      // Always use i18n.language as the active language — the user may have switched
      // via the header LanguageSwitcher after the last save in this form, and we must
      // not override that choice by re-applying a stale saved value.
      const savedDisplayPrefs = localStorage.getItem('displayPreferences');
      if (savedDisplayPrefs) {
        const parsed = JSON.parse(savedDisplayPrefs);
        setDisplayPrefs({ ...parsed, language: i18n.language });
      } else {
        setDisplayPrefs({ language: i18n.language, dateFormat: 'MM/DD/YYYY' });
      }
    } catch (err) {
      console.error('Failed to load account data:', err);
      setError('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, message) => {
    if (type === 'success') {
      setSuccess(message);
      setError(null);
    } else {
      setError(message);
      setSuccess(null);
    }
    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 5000);
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await userService.updateProfile(profile);
      await refreshCompany();
      showMessage('success', 'Profile updated successfully');
    } catch (err) {
      console.error('Failed to update profile:', err);
      showMessage('error', err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!isCompanyAdmin) {
      showMessage('error', 'You do not have permission to update company information');
      return;
    }

    try {
      setSaving(true);
      await companyService.updateCompany(company);
      updateCompanyContext(company);
      await refreshCompany();
      showMessage('success', 'Company information updated successfully');
    } catch (err) {
      console.error('Failed to update company:', err);
      showMessage('error', err.response?.data?.message || 'Failed to update company information');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    if (passwords.newPassword.length < 8) {
      showMessage('error', 'New password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      await userService.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage('success', 'Password changed successfully');
    } catch (err) {
      console.error('Failed to change password:', err);
      showMessage('error', err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    try {
      setSaving(true);
      const response = await twoFactorService.setup();
      setTwoFactorSetup(response);
      setShowTwoFactorSetup(true);
      setTwoFactorCode('');
    } catch (err) {
      console.error('Failed to setup 2FA:', err);
      showMessage('error', err.response?.data?.message || 'Failed to setup two-factor authentication');
    } finally {
      setSaving(false);
    }
  };

  const handleEnable2FA = async () => {
    if (twoFactorCode.length !== 6) {
      showMessage('error', 'Please enter a 6-digit code');
      return;
    }

    try {
      setSaving(true);
      await twoFactorService.enable(twoFactorCode);
      setTwoFactorEnabled(true);
      setShowTwoFactorSetup(false);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      showMessage('success', 'Two-factor authentication enabled successfully');
    } catch (err) {
      console.error('Failed to enable 2FA:', err);
      showMessage('error', err.response?.data?.message || 'Invalid verification code');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      showMessage('error', 'Please enter a 6-digit code');
      return;
    }

    try {
      setSaving(true);
      await twoFactorService.disable(disableCode);
      setTwoFactorEnabled(false);
      setShowDisable2FA(false);
      setDisableCode('');
      showMessage('success', 'Two-factor authentication disabled');
    } catch (err) {
      console.error('Failed to disable 2FA:', err);
      showMessage('error', err.response?.data?.message || 'Invalid verification code');
    } finally {
      setSaving(false);
    }
  };

  const copySecret = () => {
    if (twoFactorSetup?.secret) {
      navigator.clipboard.writeText(twoFactorSetup.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  // Notification preferences handlers
  const handleSaveNotificationPrefs = async () => {
    try {
      setSaving(true);
      await notificationService.updatePreferences(notificationPrefs);
      showMessage('success', 'Notification preferences saved');
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
      showMessage('error', err.response?.data?.message || 'Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisplayPrefs = () => {
    localStorage.setItem('displayPreferences', JSON.stringify(displayPrefs));
    i18n.changeLanguage(displayPrefs.language);
    updatePreferences(displayPrefs);
    showMessage('success', 'Display preferences saved');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('account.settings')}</h1>
        <p className="text-gray-600 mt-1">{t('account.manageInfo')}</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-700">{success}</span>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                <div className="relative">
                  <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                    {profile.profilePhotoUrl
                      ? <img src={profile.profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                      : <User className="w-12 h-12 text-primary-600" />
                    }
                  </div>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white hover:bg-primary-700"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t('account.profilePicture')}</h3>
                  <p className="text-sm text-gray-600 mb-3">{t('account.updateProfilePhoto')}</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="btn btn-outline text-sm"
                    >
                      {t('account.uploadNew')}
                    </button>
                    {profile.profilePhotoUrl && (
                      <button
                        type="button"
                        onClick={() => setProfile(p => ({ ...p, profilePhotoUrl: '' }))}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        {t('account.removePhoto')}
                      </button>
                    )}
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        showMessage('error', 'Photo must be smaller than 2 MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = (ev) => setProfile(p => ({ ...p, profilePhotoUrl: ev.target.result }));
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.firstName')}</label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.lastName')}</label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  {t('common.email')}
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  {t('common.phone')}
                </label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                <input
                  type="text"
                  value={profile.jobTitle}
                  onChange={(e) => setProfile({ ...profile, jobTitle: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                <textarea
                  rows={4}
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Tell us about yourself..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">My Timezone</label>
                <select
                  value={profile.timezone}
                  onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Use company default{company.timezone ? ` (${TIMEZONE_OPTIONS.find(tz => tz.value === company.timezone)?.label || company.timezone})` : ''}</option>
                  {TIMEZONE_OPTIONS.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Overrides the company timezone for every date and time shown to you.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button className="btn btn-outline" onClick={loadData}>{t('common.cancel')}</button>
                <button className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('account.saveChanges')}
                </button>
              </div>
            </div>
          )}

          {/* Company Tab */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              {!isCompanyAdmin && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-yellow-800 text-sm">
                    {t('account.companyViewOnly')}
                  </p>
                </div>
              )}

              {/* Company Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Camera className="w-4 h-4 inline mr-2" />
                  {t('account.companyLogo')}
                </label>
                <div className="flex items-center gap-5">
                  {/* Preview */}
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 flex-shrink-0">
                    {company.logoUrl ? (
                      <img src={company.logoUrl} alt="Company logo" className="w-full h-full object-contain" />
                    ) : (
                      <Building className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {isCompanyAdmin && (
                      <>
                        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors">
                          <Camera className="w-4 h-4" />
                          {t('account.uploadLogo')}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 2 * 1024 * 1024) {
                                setError('Logo must be smaller than 2 MB');
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = (ev) => setCompany(prev => ({ ...prev, logoUrl: ev.target.result }));
                              reader.readAsDataURL(file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        {company.logoUrl && (
                          <button
                            type="button"
                            onClick={() => setCompany(prev => ({ ...prev, logoUrl: '' }))}
                            className="inline-flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            {t('account.removeLogo')}
                          </button>
                        )}
                      </>
                    )}
                    <p className="text-xs text-gray-500">{t('account.logoHint')}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  {t('account.companyName')}
                </label>
                <input
                  type="text"
                  value={company.name}
                  onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  disabled={!isCompanyAdmin}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.industry')}</label>
                  <select
                    value={company.industry}
                    onChange={(e) => setCompany({ ...company, industry: e.target.value })}
                    disabled={!isCompanyAdmin}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                  >
                    <option value="">{t('account.selectIndustry')}</option>
                    {INDUSTRY_OPTIONS.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.companySize')}</label>
                  <select
                    value={company.size}
                    onChange={(e) => setCompany({ ...company, size: e.target.value })}
                    disabled={!isCompanyAdmin}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                  >
                    <option value="">{t('account.selectSize')}</option>
                    {COMPANY_SIZE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {t('common.address')}
                </label>
                <input
                  type="text"
                  value={company.address}
                  onChange={(e) => setCompany({ ...company, address: e.target.value })}
                  disabled={!isCompanyAdmin}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 mb-3 disabled:bg-gray-100"
                  placeholder={t('account.streetAddress')}
                />
                <div className="grid md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    value={company.city}
                    onChange={(e) => setCompany({ ...company, city: e.target.value })}
                    disabled={!isCompanyAdmin}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                    placeholder={t('common.city')}
                  />
                  <input
                    type="text"
                    value={company.state}
                    onChange={(e) => setCompany({ ...company, state: e.target.value })}
                    disabled={!isCompanyAdmin}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                    placeholder={t('common.state')}
                  />
                  <input
                    type="text"
                    value={company.zipCode}
                    onChange={(e) => setCompany({ ...company, zipCode: e.target.value })}
                    disabled={!isCompanyAdmin}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                    placeholder={t('common.postalCode')}
                  />
                  <input
                    type="text"
                    value={company.country}
                    onChange={(e) => {
                      const newCountry = e.target.value;
                      const suggestedTz = getTimezoneForCountry(newCountry);
                      setCompany({
                        ...company,
                        country: newCountry,
                        ...(suggestedTz && !company.timezone ? { timezone: suggestedTz } : {}),
                      });
                    }}
                    onBlur={() => {
                      const suggestedTz = getTimezoneForCountry(company.country);
                      if (suggestedTz && !company.timezone) {
                        setCompany({ ...company, timezone: suggestedTz });
                      }
                    }}
                    disabled={!isCompanyAdmin}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                    placeholder={t('common.country')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-2" />
                  {t('common.website')}
                </label>
                <input
                  type="url"
                  value={company.website}
                  onChange={(e) => setCompany({ ...company, website: e.target.value })}
                  disabled={!isCompanyAdmin}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                  placeholder="https://example.com"
                />
              </div>

              {isCompanyAdmin && (
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button className="btn btn-outline" onClick={loadData}>{t('common.cancel')}</button>
                  <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving}>
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {t('account.saveChanges')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Password Section */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-1">Password Security</h4>
                <p className="text-sm text-gray-600">Use a strong password with at least 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.currentPassword')}</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwords.currentPassword}
                    onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.newPassword')}</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.confirmPassword')}</label>
                <input
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                {passwords.newPassword && passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  className="btn btn-outline"
                  onClick={() => setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' })}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleChangePassword}
                  disabled={saving || !passwords.currentPassword || !passwords.newPassword || passwords.newPassword !== passwords.confirmPassword}
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('common.update')}
                </button>
              </div>

              {/* Two-Factor Authentication Section */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg mt-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-primary-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">{t('account.twoFactor')}</h4>
                    <p className="text-sm text-gray-600">
                      {twoFactorEnabled
                        ? 'Two-factor authentication is enabled for your account.'
                        : 'Add an extra layer of security to your account using an authenticator app.'}
                    </p>
                  </div>
                </div>

                {twoFactorEnabled ? (
                  <div className="flex items-center gap-3 mt-4">
                    <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Enabled
                    </span>
                    <button
                      className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => setShowDisable2FA(true)}
                    >
                      {t('account.disable2FA')}
                    </button>
                  </div>
                ) : (
                  <button className="btn btn-primary mt-4" onClick={handleSetup2FA} disabled={saving}>
                    <Smartphone className="w-4 h-4 mr-2" />
                    {t('account.enable2FA')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('account.notificationPrefs')}</h3>
                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900">{t('account.emailNotifications')}</div>
                      <div className="text-sm text-gray-600">{t('account.emailNotificationsDesc')}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationPrefs.emailEnabled}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailEnabled: e.target.checked })}
                      className="rounded"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900">{t('account.workOrderUpdates')}</div>
                      <div className="text-sm text-gray-600">{t('account.workOrderUpdatesDesc')}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationPrefs.workOrderUpdates}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, workOrderUpdates: e.target.checked })}
                      className="rounded"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900">{t('account.maintenanceReminders')}</div>
                      <div className="text-sm text-gray-600">{t('account.maintenanceRemindersDesc')}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationPrefs.maintenanceReminders}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, maintenanceReminders: e.target.checked })}
                      className="rounded"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900">{t('account.assetAlerts')}</div>
                      <div className="text-sm text-gray-600">{t('account.assetAlertsDesc')}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationPrefs.assetAlerts}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, assetAlerts: e.target.checked })}
                      className="rounded"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900">{t('account.subscriptionAlerts')}</div>
                      <div className="text-sm text-gray-600">{t('account.subscriptionAlertsDesc')}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationPrefs.subscriptionAlerts}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, subscriptionAlerts: e.target.checked })}
                      className="rounded"
                    />
                  </label>

                  <div className="border border-gray-200 rounded-lg">
                    <label className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50">
                      <div>
                        <div className="font-medium text-gray-900">{t('account.weeklyReports')}</div>
                        <div className="text-sm text-gray-600">{t('account.weeklyReportsDesc')}</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={notificationPrefs.weeklyReports}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, weeklyReports: e.target.checked })}
                        className="rounded"
                      />
                    </label>
                    {notificationPrefs.weeklyReports && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-2 mb-3">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">{t('account.reportSchedule')}</span>
                        </div>
                        {isCompanyAdmin ? (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t('account.dayOfWeek')}</label>
                              <select
                                value={notificationPrefs.weeklyReportDay}
                                onChange={(e) => setNotificationPrefs({ ...notificationPrefs, weeklyReportDay: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              >
                                {WEEKDAY_OPTIONS.map(day => (
                                  <option key={day.value} value={day.value}>{day.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t('account.reportTime')}</label>
                              <input
                                type="time"
                                value={notificationPrefs.weeklyReportTime}
                                onChange={(e) => setNotificationPrefs({ ...notificationPrefs, weeklyReportTime: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            {t('account.weeklyReportScheduleInfo', { day: WEEKDAY_OPTIONS.find(d => d.value === notificationPrefs.weeklyReportDay)?.label || 'Monday', time: notificationPrefs.weeklyReportTime || '09:00' })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900">{t('account.systemAnnouncements')}</div>
                      <div className="text-sm text-gray-600">{t('account.systemAnnouncementsDesc')}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationPrefs.systemAnnouncements}
                      onChange={(e) => setNotificationPrefs({ ...notificationPrefs, systemAnnouncements: e.target.checked })}
                      className="rounded"
                    />
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 mt-4">
                  <button className="btn btn-primary" onClick={handleSaveNotificationPrefs} disabled={saving}>
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {t('account.saveNotificationSettings')}
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('account.displayPrefs')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.language')}</label>
                    <select
                      value={displayPrefs.language}
                      onChange={(e) => setDisplayPrefs({ ...displayPrefs, language: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="en">English</option>
                      <option value="ar">Arabic ({'\u0627\u0644\u0639\u0631\u0628\u064A\u0629'})</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.timezone')}</label>
                    {isCompanyAdmin ? (
                      <select
                        value={company.timezone}
                        onChange={(e) => setCompany({ ...company, timezone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">{t('account.selectTimezone')}</option>
                        {TIMEZONE_OPTIONS.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={TIMEZONE_OPTIONS.find(tz => tz.value === company.timezone)?.label || company.timezone || 'Not set'}
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('account.timezoneSetByAdmin')}</p>
                      </>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('account.dateFormat')}</label>
                    <select
                      value={displayPrefs.dateFormat}
                      onChange={(e) => setDisplayPrefs({ ...displayPrefs, dateFormat: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 mt-4">
                  <button className="btn btn-primary" onClick={() => { handleSaveDisplayPrefs(); if (isCompanyAdmin) handleSaveCompany(); }}>
                    <Save className="w-4 h-4 mr-2" />
                    {t('account.saveDisplaySettings')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <h3 className="text-lg font-bold text-red-600 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">Irreversible and destructive actions</p>
        <button className="btn bg-red-600 hover:bg-red-700 text-white">
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Account
        </button>
      </div>

      {/* 2FA Setup Modal */}
      {showTwoFactorSetup && twoFactorSetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Set Up Two-Factor Authentication</h3>
              <button onClick={() => setShowTwoFactorSetup(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>

              <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFactorSetup.qrCodeUrl)}`}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Or enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-white px-3 py-2 rounded border border-gray-200">
                    {twoFactorSetup.secret}
                  </code>
                  <button
                    onClick={copySecret}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                  >
                    {secretCopied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter the 6-digit code from your authenticator app
                </label>
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  maxLength={6}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button className="btn btn-outline flex-1" onClick={() => setShowTwoFactorSetup(false)}>
                  {t('common.cancel')}
                </button>
                <button
                  className="btn btn-primary flex-1"
                  onClick={handleEnable2FA}
                  disabled={saving || twoFactorCode.length !== 6}
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                  {t('account.enable2FA')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisable2FA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Disable Two-Factor Authentication</h3>
              <button onClick={() => setShowDisable2FA(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  Disabling 2FA will make your account less secure. Enter your current 2FA code to confirm.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter the 6-digit code from your authenticator app
                </label>
                <input
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  maxLength={6}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button className="btn btn-outline flex-1" onClick={() => setShowDisable2FA(false)}>
                  {t('common.cancel')}
                </button>
                <button
                  className="btn bg-red-600 hover:bg-red-700 text-white flex-1"
                  onClick={handleDisable2FA}
                  disabled={saving || disableCode.length !== 6}
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {t('account.disable2FA')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
